import { Box, Stack, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import TodayIcon from '@mui/icons-material/Today';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import EmojiPeopleIcon from '@mui/icons-material/EmojiPeople';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import BlockIcon from '@mui/icons-material/Block';
import { formatNameShort } from '@shared/entity';
import { VisitRow, Autor } from '../../entity';
import VisitActionButtons from './visits-list-row-actions';
import { VisitListStatusIcon } from './visit-list-status-icon';

export const createVisitsColumns = (
  onDateHeaderClick?: (dateLabel: string) => void,
): GridColDef<VisitRow>[] => [
  createTimeColumn(onDateHeaderClick),
  createClientServiceColumn(),
  createVisitStateColumn(),
  createActionsColumn(),
];

const createTimeColumn = (
  onDateHeaderClick?: (dateLabel: string) => void,
): GridColDef<VisitRow> => ({
  field: 'date',
  headerName: 'stock.time',
  width: 45,
  hideSortIcons: false,
  display: 'flex',
  flex: 1.5,
  minWidth: 20,
  renderCell: params => {
    if (params.row.isHeader) {
      const headerRow = params.row;

      return (
        <Box
          sx={{
            textAlign: 'center',
            fontWeight: 'bold',
            position: 'absolute',
            display: 'flex',
            padding: '0',
            left: '10px',
            gap: '10px',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          {headerRow.label}
          {onDateHeaderClick && <TodayIcon sx={{ width: '17px' }} />}
        </Box>
      );
    }

    if (params.row.isPlaceholder) {
      const isHoliday = params.row.placeholderKind === 'holiday';
      const isNonWorking = params.row.placeholderKind === 'nonWorking';
      return (
        <Box
          sx={{
            position: 'absolute',
            left: '10px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            color: isHoliday ? 'info.main' : isNonWorking ? 'warning.dark' : 'text.secondary',
            fontStyle: 'italic',
            fontSize: '12px',
          }}
        >
          {isHoliday ? (
            <BeachAccessIcon sx={{ width: '14px' }} />
          ) : isNonWorking ? (
            <BlockIcon sx={{ width: '14px' }} />
          ) : (
            <EventBusyIcon sx={{ width: '14px' }} />
          )}
          {params.row.label}
        </Box>
      );
    }

    return (
      <Stack>
        <Typography fontSize="12px">{params.row.date}</Typography>
        <Typography color="text.secondary" fontSize="12px">
          {params.row.dateTo}
        </Typography>
      </Stack>
    );
  },
});

const createClientServiceColumn = (): GridColDef<VisitRow> => ({
  field: 'client',
  headerName: 'stock.customer',
  display: 'flex',
  flex: 4.5,
  minWidth: 70,
  renderCell: params => {
    const isHeader = params.row.isHeader;
    const isPlaceholder = params.row.isPlaceholder;
    const showDepositIcon = !isHeader && !isPlaceholder && params.row.clientDeposit;
    const visitDepositPayed = !isHeader && !isPlaceholder && params.row.visitDepositPayed;

    if (isHeader || isPlaceholder) {
      return null;
    }

    return (
      <Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography fontSize="12px" fontWeight="bold">
            {formatNameShort(params.value)}
          </Typography>
          {showDepositIcon &&
            (visitDepositPayed ? (
              <CreditScoreIcon sx={{ fontSize: '15px' }} color="success" />
            ) : (
              <CreditCardOffIcon sx={{ fontSize: '15px' }} color="error" />
            ))}
          {params.row.autor === Autor.CUSTOMER && (
            <EmojiPeopleIcon sx={{ fontSize: '15px' }} color="info" />
          )}
        </Stack>
        <Typography color="text.secondary" fontSize="12px">
          {params.row.serviceName}
        </Typography>
      </Stack>
    );
  },
});

const createVisitStateColumn = (): GridColDef<VisitRow> => ({
  field: 'visitState',
  headerName: 'stock.visitState',
  width: 70,
  flex: 1.8,
  display: 'flex',
  editable: false,
  renderCell: params => {
    if (params.row.isHeader || params.row.isPlaceholder) {
      return null;
    }

    const isVisitOpen = params.row.visitState;
    const { clientId, id: visitId } = params.row;

    return <VisitListStatusIcon isVisitOpen={isVisitOpen} clientId={clientId} visitId={visitId} />;
  },
});

const createActionsColumn = (): GridColDef<VisitRow> => ({
  field: 'visitDetailButton',
  headerName: 'stock.visitDetailButton',
  width: 10,
  flex: 3,
  display: 'flex',
  editable: false,
  renderCell: params => {
    if (params.row.isHeader || params.row.isPlaceholder) {
      return null;
    }

    return <VisitActionButtons clientId={params.row.clientId} visitId={params.row.id} />;
  },
});
